import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import styled from 'styled-components';
import { Modal } from 'app-components';

import Form from '../../../lib/wrappers/Form';
import { useExchangeConditions, useModal } from '../../../hooks';
import { ENROLLMENT_ACTIONS, PLAN_TYPE, FROM_TAB } from './modalTypes';
import { ModalContextType } from '../../../contexts/modalContext';
import UniversalInput from '../../../lib/wrappers/UniversalInput';
import { INPUT_TYPES } from '../../../constants/inputs/inputTypes';
import { ConditionalRendering } from '../../../components/common/conditional-rendering';
import { IDisenrollGroup } from '../types/customGroupingTypes';
import CapitalizedButton from '../components/common/CapitalizedButton';

const TextAreaContainer = styled.div`
    .gi-textarea__input {
        width: 100%;  // For the textarea to take up the full width of the container
    }
    .gi-textarea {
        margin-top: -1rem; // To align the textarea with the radio buttons
    }
`;

const StyledWarningContainer = styled.div`
    border-left: 3px solid ${({ theme }) => theme.infoMain} !important;
    color : ${({ theme }) => theme.infoDarker} !important;
    background : ${({ theme }) => theme.infoLightest} !important;
`;


interface IModalProps extends ModalContextType {
    typeOfPlan: PLAN_TYPE;
    enrollmentAction: ENROLLMENT_ACTIONS;
    group: IDisenrollGroup;
    dentalDetails?: IDisenrollGroup;
    fromTab?: FROM_TAB;
    setSelectedReason?: Function
}
interface IDisenrollmentState {
    reason: undefined | string;
    reasonOther: string;
}

const NJ_STATE_OPTIONS = ['EMPLOYER_INSURANCE', 'MEDICARE', 'NJFAMILYCARE', 'OTHER_HEALTH_COVERAGE', 'DEATH', 'MOVING_OUT_OF_STATE'];

export default function DisenrollOptionsModal({ typeOfPlan, enrollmentAction, closeModal, group, dentalDetails, fromTab, setSelectedReason }: IModalProps) {
    const intl = useIntl();
    const { openModal } = useModal();

    const { isDisenrollOptionsVisible, isReportLifeChangeEventVisible } = useExchangeConditions();

    const [disenrollmentState, setDisenrollmentState] = useState<IDisenrollmentState>({ reason: undefined, reasonOther: '' });

    function onClose(e) {
        e?.preventDefault();
        closeModal('disenrollOptions');
    };

    function getSelectedReason() {
        if (disenrollmentState?.reason && NJ_STATE_OPTIONS.includes(disenrollmentState?.reason) || !disenrollmentState?.reason) {
            return 'NO_OPTION_SELECTED';
        } else {
            return disenrollmentState.reason;
        }
    }

    function onContinue() {
        const selectedReason = getSelectedReason() || !disenrollmentState.reason;
        if (FROM_TAB.ENROLLMENTHISTORY === fromTab && setSelectedReason) {
            setSelectedReason({ ...disenrollmentState, 'reason': selectedReason });
            closeModal('disenrollOptions');
        } else {
            openModal('disenrollmentConfirmation', { typeOfPlan, enrollmentAction, group, fromTab, dentalDetails, disenrollmentState: { reason: selectedReason, reasonOther: disenrollmentState.reasonOther } });
        }
    }

    const options = useMemo(
        () => [
            {
                value: 'CANNOT_AFFORD_PAYMENT',
                text: 'indportal.disenrollOptions.input.option.icantafford',
            },
            {
                value: 'NO_PROPER_SERVICE',
                text: 'indportal.disenrollOptions.input.option.imnothappywith',
            },
            {
                value: 'PHY_OUT_OF_NETWORK',
                text: 'indportal.disenrollOptions.input.option.phyOutOfNetwork',
            },
            {
                value: 'AGENT_ERROR',
                text: 'indportal.disenrollOptions.input.option.agentError',
            },
            ...((isDisenrollOptionsVisible && fromTab !== FROM_TAB.PLANSUMMARY)
                ? [
                    {
                        value: 'EMPLOYER_INSURANCE',
                        text: 'indportal.disenrollOptions.input.option.coveredByEmployerInsurance',
                    },
                    {
                        value: 'MEDICARE',
                        text: 'indportal.disenrollOptions.input.option.coveredByMedicare',
                    },
                    {
                        value: 'NJFAMILYCARE',
                        text: 'indportal.disenrollOptions.input.option.coveredByNJFamilyCare',
                    },
                    {
                        value: 'OTHER_HEALTH_COVERAGE',
                        text: 'indportal.disenrollOptions.input.option.coveredByOtherHealthCoverage',
                    },
                    {
                        value: 'DEATH',
                        text: 'indportal.disenrollOptions.input.option.death',
                    },
                    {
                        value: 'MOVING_OUT_OF_STATE',
                        text: 'indportal.disenrollOptions.input.option.movingoutofstate',
                    }
                ]
                : []),
            {
                value: 'OTHER',
                text: 'indportal.disenrollOptions.input.option.other',
            },
        ],
        [],
    );

    function getEnrollmentActionLabel(enrollmentAction) {
        return `${intl.formatMessage({ id: `indportal.disenrollOptions.input.legend.${enrollmentAction}` })}`;
    }

    return (
        <Modal id="disenrollConfirmation">
            <Modal.Header
                id="disenrollConfirmationHeader"
                titleId={`indportal.disenrollOptions.header.${enrollmentAction}`}
                onHide={onClose}
            />
            <Modal.Body id="disenrollConfirmationBody">
                <Form formName="disenrollOptionsForm">
                    <UniversalInput
                        legend={{
                            legendText: <span dangerouslySetInnerHTML={{ __html: getEnrollmentActionLabel(enrollmentAction) }}></span>
                        }}
                        label={{
                            showRequiredIcon: false,
                            ulClass: ['margin-b-20  margin-l-20'],
                        }}
                        inputData={{
                            isInputRequired: true,
                            type: INPUT_TYPES.RADIO_BUTTONS,
                            liClass: ['text-left'],
                            currentData: disenrollmentState,
                            name: 'reason',
                            value: disenrollmentState.reason,
                            options,
                            optionsReplacements: {
                                planType: intl.formatMessage({ id: `indportal.disenrollOptions.${typeOfPlan}` }),
                            },
                        }}
                        errorClasses={['margin-l-0']}
                        inputActions={{
                            onFieldChange: ({ reason }) => {
                                setDisenrollmentState(prev => ({ ...prev, reason }));
                            },
                        }}
                    />
                    <ConditionalRendering isTrue={disenrollmentState.reason === 'OTHER'}>
                        <TextAreaContainer>
                            <UniversalInput
                                label={{
                                    isLabelVisible: false,
                                }}
                                inputData={{
                                    type: INPUT_TYPES.TEXTAREA,
                                    liClass: ['text-left'],
                                    currentData: disenrollmentState,
                                    name: 'reasonOther',
                                    value: disenrollmentState.reasonOther,
                                }}
                                errorClasses={['margin-l-0']}
                                inputActions={{
                                    onFieldChange: ({ reasonOther }) => {
                                        setDisenrollmentState(prev => ({ ...prev, reasonOther }));
                                    },
                                }}
                            />
                        </TextAreaContainer>
                    </ConditionalRendering>
                </Form>
                <ConditionalRendering isTrue={isReportLifeChangeEventVisible}>
                    <StyledWarningContainer className="alert" dangerouslySetInnerHTML={{ __html: intl.formatMessage({ id: 'indportal.disenrollOptions.warning' }) }}>
                    </StyledWarningContainer>
                </ConditionalRendering>
            </Modal.Body>
            <Modal.Controls>
                <CapitalizedButton
                    id="disenrollConfirmationModalCancelButton"
                    onClickHandler={onClose}
                    variant="secondary"
                    labelId="button.cancel"
                />
                <ConditionalRendering isTrue={!!disenrollmentState.reason}>
                    <CapitalizedButton
                        id="disenrollConfirmationModalContinueButton"
                        onClickHandler={onContinue}
                        variant="primary"
                        labelId="button.continue"
                        leftMargin={true}
                    />
                </ConditionalRendering>
                <ConditionalRendering isTrue={!disenrollmentState.reason}>
                    <CapitalizedButton
                        id="disenrollConfirmationModalSkipAndContinueButton"
                        onClickHandler={onContinue}
                        variant="primary"
                        labelId="customGrouping.skipAndContinue"
                        leftMargin={true}
                    />
                </ConditionalRendering>
            </Modal.Controls>
        </Modal>
    );
}
